import TileButton from 'components/atoms/TileButton';
import Barchart from 'components/molecules/Barchart';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import Legend from 'components/molecules/Legend';
import MultiLineChartPriceCurve from 'components/molecules/MultiLineChartPriceCurve';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import React, { FC, useContext, useMemo, useRef, useState } from 'react';
import {
    calculateContainerHeight,
    fetchCurrency,
    formatNumWithKMB,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getConfig,
} from 'utils/helperMethods';
import { CustomSelect } from '../BumpReport/MainSection';
import DownSvg from 'assets/svg/Down';
import { UserContext } from 'contexts/ConfigContext';
import { colorConstants } from 'config/colorConstants';
import { commonClassName, commonHeaderClass } from 'components/pages/PromoScorecard';
import { subHeaderClass, yAxisLabelClass } from '../GrowthAnalysis/ExpandedView';
import { yAxisWidth } from '../PriceLadder/BarPointChartContainer';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { salesGraphWidth } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { priceCurveChartDownload } from 'parser/graphToExcelDownloadParser';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { FilterContextData } from 'contexts/FilterContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PRICE_CURVE_CHART_LEGEND_CLICKED,
    PRICE_CURVE_LEVELS_DROPDOWN_CHANGED,
    PRICE_CURVE_TAB_CHANGED,
    PRICE_CURVE_X_AXIS_DROPDOWN_CHANGED,
    PRICE_CURVE_Y_AXIS_DROPDOWN_CHANGED,
} from 'utils/mixpanelEventNames';
import { ConfigKeys } from 'utils/Enums';

interface Props {
    memoisedData: {
        data: any;
        legends: any[];
        min: number;
        max: number;
        xAxisValues: any[];
    };
    widthConfig: {
        containerWidth: number;
    };
    dropdownList: {
        xAxis: any;
        yAxis: any;
        levels: any;
    };
    xAxisSelectedValue: any;
    yAxisSelectedValue: any;
    levelsSelectedValue: any;
    setXAxisSelectedValue: Function;
    setYAxisSelectedValue: Function;
    setLevelsSelectedValue: Function;
    legendData: any;
    selectedLegends: any[];
    setSelectedLegends: Function;
    subTileState: any;
    setSubTileState: Function;
    loading: boolean;
    error: any;
}

export const priceCurveMultiLineId = 'price-curve-line-chart-wrapper';

const chartHeight = calculateContainerHeight(2, 100);

const PriceCurveMultiLineChartContainer: FC<Props> = ({
    memoisedData,
    levelsSelectedValue,
    setLevelsSelectedValue,
    setXAxisSelectedValue,
    setYAxisSelectedValue,
    xAxisSelectedValue,
    yAxisSelectedValue,
    legendData,
    selectedLegends,
    setSelectedLegends,
    widthConfig,
    dropdownList,
    subTileState,
    setSubTileState,
    loading,
    error,
}) => {
    const { appConfig } = useContext(UserContext);
    const priceCurveConfig = getConfig('priceCurve', appConfig);
    const { decimalForNonPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { xAxis: xAxisDropdownValues, yAxis: yAxisDropdownValues, levels: levelsDropdownValues } = dropdownList;
    //width config
    const { containerWidth } = widthConfig;
    const { allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);
    const chartCopyRef = useRef<any>();
    const graphViewContainerWidth =
        Math.max(memoisedData?.xAxisValues ? memoisedData?.xAxisValues?.length * salesGraphWidth : 0, containerWidth) ||
        window.innerWidth;
    const perGraphComponentWidth = graphViewContainerWidth / memoisedData?.xAxisValues?.length || 0;

    function infiniteScrollOnNext(width: number, loading: boolean) {}
    const mainLabel = `${yAxisSelectedValue?.label} curve across ${xAxisSelectedValue?.label} and ${levelsSelectedValue?.label}`;
    const currency = fetchCurrency(appConfig) || '';

    const chartExcelData = useMemo(() => {
        return priceCurveChartDownload(
            memoisedData?.data,
            memoisedData?.xAxisValues,
            yAxisSelectedValue,
            xAxisSelectedValue,
        );
    }, [memoisedData]);
    return (
        <div>
            <div className="my-6 flex justify-between items-center">
                <div className="flex gap-4">
                    <div>
                        <label className="mr-2 text-greyFont font-xs">X Axis</label>
                        <CustomSelect
                            className="dropdown-height dropdown-width-alt uniqSelPCXAxisDropdown"
                            suffixIcon={<DownSvg />}
                            value={xAxisSelectedValue?.value}
                            disabled={loading}
                            options={xAxisDropdownValues}
                            onChange={(val: any, item: any) => {
                                setXAxisSelectedValue(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_CURVE_X_AXIS_DROPDOWN_CHANGED, {
                                    xAxisName: item?.label,
                                });
                            }}
                        />
                    </div>
                    <div>
                        <label className="mr-2 text-greyFont font-xs">Y Axis</label>
                        <CustomSelect
                            className="dropdown-height dropdown-width-alt uniqSelPCYAxisDropdown"
                            suffixIcon={<DownSvg />}
                            value={yAxisSelectedValue?.value}
                            disabled={loading}
                            options={yAxisDropdownValues}
                            onChange={(val: any, item: any) => {
                                setYAxisSelectedValue(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_CURVE_Y_AXIS_DROPDOWN_CHANGED, {
                                    yAxisName: item?.label,
                                });
                            }}
                        />
                    </div>
                    <div>
                        <label className="mr-2 text-greyFont font-xs">Levels</label>
                        <CustomSelect
                            className="dropdown-height dropdown-width-alt uniqSelPCLevelsDropdown"
                            suffixIcon={<DownSvg />}
                            value={levelsSelectedValue?.value}
                            disabled={loading}
                            options={levelsDropdownValues}
                            onChange={(val: any, item: any) => {
                                setLevelsSelectedValue(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_CURVE_LEVELS_DROPDOWN_CHANGED, {
                                    levelsName: item?.label,
                                });
                            }}
                        />
                    </div>
                </div>
                <div>
                    <TileButton
                        disabled={loading}
                        onClick={(item: any) => {
                            setSubTileState(item);
                            //mixpanel event
                            trackEventMixpanel(PRICE_CURVE_TAB_CHANGED, {
                                tabName: item?.label,
                            });
                        }}
                        activeKey={subTileState.key}
                        data={priceCurveConfig.subTileData}
                    />
                </div>
            </div>
            {loading ? (
                <div style={{ height: chartHeight + 50 }}>
                    <Loader />
                </div>
            ) : error ? (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: chartHeight + 50,
                    }}
                >
                    <ErrorBox msg={String(error)} />
                </div>
            ) : (
                <div ref={chartCopyRef} className={commonClassName}>
                    <div className="flex justify-between items-center">
                        <h2 className={`${subHeaderClass}`}>{mainLabel}</h2>
                        <div className="flex items-center justify-center gap-2">
                            <CopyToClipBoard refOfComponent={chartCopyRef} />
                            <DownloadExcel
                                title={'PriceCurveChart'}
                                excelData={chartExcelData.data}
                                headers={chartExcelData.headers}
                            />
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <Legend
                            style={{
                                justifyContent: 'initial',
                                marginInline: 'auto',
                                width: 'fit-content',
                                marginBlock: 4,
                                padding: 4,
                            }}
                            className="legend-size-square flex-none"
                            // itemClassName="flex-none"
                            data={legendData?.legendData}
                            onClick={(item: any) => {
                                const itemIndex = selectedLegends.findIndex((legend) => legend === item?.label);
                                //if item already present, then remove it
                                if (itemIndex > -1) {
                                    const tempLegends = [...selectedLegends];
                                    tempLegends.splice(itemIndex, 1);
                                    //mixpanel event
                                    trackEventMixpanel(PRICE_CURVE_CHART_LEGEND_CLICKED, {
                                        legendName: item?.label,
                                        isSelected: false,
                                        tabName: subTileState?.label,
                                    });
                                    return setSelectedLegends(tempLegends);
                                } else if (selectedLegends.length === 2) return;
                                //else set the new item
                                setSelectedLegends([...selectedLegends, item.label]);

                                //mixpanel event
                                trackEventMixpanel(PRICE_CURVE_CHART_LEGEND_CLICKED, {
                                    legendName: item?.label,
                                    isSelected: true,
                                    tabName: subTileState?.label,
                                });
                            }}
                            selected={memoisedData?.legends}
                        />
                    </div>
                    <p className={`${yAxisLabelClass} mb-4`}>{yAxisSelectedValue?.label}</p>
                    <div className="flex">
                        <Barchart
                            range={[0.03, 0.98]}
                            padding={[0, 0, 0, 0]}
                            barWidth={30}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`price-curve-y1`}
                            fields={['date', 'value']}
                            //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                            maxBound={memoisedData?.max}
                            minBound={memoisedData?.min}
                            width={yAxisWidth}
                            colorKey={'type'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                            }}
                            // Yline
                            YGrid={false}
                        />
                        <HorizontalInfiniteScroller
                            id={priceCurveMultiLineId}
                            hasMore={false}
                            scrollThreshold={0.9}
                            toScrollWidth={0}
                            onNext={(width: number) => {
                                infiniteScrollOnNext(width, false);
                            }}
                        >
                            <div
                                id={priceCurveMultiLineId}
                                style={{ paddingBottom: 45, overflowX: 'auto', maxWidth: '100%' }}
                                className=""
                            >
                                <MultiLineChartPriceCurve
                                    range={[0.02, 0.98]}
                                    showLegend
                                    padding={[0, 0, 5, 0]}
                                    // yAxisConfig={false}
                                    dataset={memoisedData?.data || []}
                                    height={chartHeight}
                                    loading={false}
                                    chartContainer={`price-curve-line-chart`}
                                    fields={['xAxisData', yAxisSelectedValue?.value]}
                                    maxBound={memoisedData?.max}
                                    minBound={memoisedData?.min}
                                    width={graphViewContainerWidth} //subtracting the padding
                                    xAxisLabelFormatter={(val: any) => {
                                        return val;
                                    }}
                                    size={6}
                                    colorMapping={legendData?.colorMapping}
                                    colorKey={'color'}
                                    tooltipFormatter={(xAxis, val, sales) => {
                                        return {
                                            name: xAxis,
                                            value: `${
                                                currency +
                                                formatNumWithKMBAlt(
                                                    val,
                                                    decimalForNonPercentage,
                                                    decimalForNonPercentage,
                                                )
                                            }, ${'&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'}${formatNumWithKMBAlt(
                                                sales,
                                                decimalForNonPercentage,
                                                decimalForNonPercentage,
                                            )}`,
                                        };
                                    }}
                                />
                                <div className="flex">
                                    {memoisedData?.xAxisValues?.map((item, index) => {
                                        return (
                                            <div
                                                style={{
                                                    width: perGraphComponentWidth,
                                                    minWidth: perGraphComponentWidth,
                                                    overflowWrap: 'anywhere',
                                                    fontSize: graphXAxisLabelFontSize,
                                                }}
                                                className="text-center p-[2px]"
                                                key={index}
                                            >
                                                {item}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </HorizontalInfiniteScroller>
                    </div>
                    <p className={`text-center !top-[-42px] mb-[-35px] relative ${yAxisLabelClass}`}>
                        {xAxisSelectedValue?.label}
                    </p>
                </div>
            )}
        </div>
    );
};

export default PriceCurveMultiLineChartContainer;
